import { adicionarLogo } from "../../services/pdf-logo";
import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FaFilePdf, FaCheckSquare, FaRegSquare } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../services/api";
import { janelaDaLocacao } from './periodo';
import { obterIdTenent } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";
import { toast } from "react-toastify";
import logo from "../../images/s.png";
import { useNomeCaucao } from '../../hooks/useNomeCaucao';

interface ModalRelatorioLocacaoProps {
    show: boolean;
    onClose: () => void;
    locacao: any;
}

interface SecaoConfig {
    key: string;
    label: string;
    descricao: string;
    ativo: boolean;
}

const SECOES_PADRAO: SecaoConfig[] = [
    { key: 'dadosLocacao', label: 'Dados da Locação', descricao: 'Status, período, valores, periodicidade', ativo: true },
    { key: 'locatario', label: 'Locatário', descricao: 'Nome, documento, telefone, endereço, CNH', ativo: true },
    { key: 'veiculo', label: 'Veículo', descricao: 'Placa, marca, modelo, ano, cor, km', ativo: true },
    { key: 'substituicoes', label: 'Substituições de Veículo', descricao: 'Trocas de veículo com KM de saída e entrada', ativo: true },
    { key: 'contrato', label: 'Contrato e Observações', descricao: 'Fiador, opção de compra, observações', ativo: true },
    { key: 'financeiro', label: 'Financeiro', descricao: 'Pagamentos, lançamentos, entradas e saídas', ativo: true },
    { key: 'multas', label: 'Multas', descricao: 'Multas de trânsito vinculadas', ativo: true },
    { key: 'manutencoes', label: 'Manutenções', descricao: 'Serviços realizados no veículo', ativo: true },
    { key: 'trocasOleo', label: 'Trocas de Óleo', descricao: 'Registros de troca de óleo', ativo: true },
    { key: 'vistorias', label: 'Vistorias', descricao: 'Vistorias do veículo realizadas', ativo: true },
];

const formatarData = (data: any): string => {
    if (!data) return '-';
    const d = new Date(data);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR');
};

/** Início e término da locação com a hora, quando ela foi registrada (locações antigas só têm o dia). */
const formatarDataHoraLocacao = (data: any): string => {
    if (!data) return '-';
    const d = new Date(data);
    if (isNaN(d.getTime())) return '-';
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
    return hora === '00:00' ? d.toLocaleDateString('pt-BR') : `${d.toLocaleDateString('pt-BR')} ${hora}`;
};

const formatarMoeda = (valor: any): string => {
    const num = Number(valor);
    if (isNaN(num)) return 'R$ 0,00';
    return `R$ ${num.toFixed(2).replace('.', ',')}`;
};

const statusLocacao = (status: number): string => {
    switch (status) {
        case 1: return 'Ativa';
        case 2: return 'Inativa';
        case 3: return 'Encerrada';
        default: return '-';
    }
};

const periodicidade = (tipo: number): string => {
    switch (tipo) {
        case 1: return 'Semanal';
        case 2: return 'Quinzenal';
        case 3: return 'Mensal';
        case 4: return 'Diario';
        default: return '-';
    }
};

const statusMulta = (status: number): string => {
    switch (status) {
        case 1: return 'Paga';
        case 2: return 'Vencida';
        case 3: return 'Em Aberto';
        case 4: return 'Cancelada';
        default: return '-';
    }
};

const statusPagamento = (status: number): string => {
    switch (status) {
        case 1: return 'Pago';
        case 2: return 'Pendente';
        case 3: return 'Atrasado';
        default: return '-';
    }
};

const ModalRelatorioLocacao: React.FC<ModalRelatorioLocacaoProps> = ({ show, onClose, locacao }) => {
    const nomeCaucao = useNomeCaucao();
    const [secoes, setSecoes] = useState<SecaoConfig[]>(SECOES_PADRAO.map(s => ({ ...s })));
    const [gerando, setGerando] = useState(false);
    const token = useAppSelector(state => state.token);

    React.useEffect(() => {
        if (show) setSecoes(SECOES_PADRAO.map(s => ({ ...s })));
    }, [show]);

    const toggleSecao = (key: string) => {
        setSecoes(prev => prev.map(s => s.key === key ? { ...s, ativo: !s.ativo } : s));
    };

    const marcarTodas = (valor: boolean) => {
        setSecoes(prev => prev.map(s => ({ ...s, ativo: valor })));
    };

    const secoesAtivas = secoes.filter(s => s.ativo);

    const obterLogoDaEmpresa = async () => {
        try {
            const response = await api.get(`Empresa/ObterURLLogo/${obterIdTenent(token)}`);
            return response.data;
        } catch {
            return "https://locasmartpro.com.br/lovable-uploads/1a30b1bd-1b9b-44d2-81b1-9dd18a301a5b.png";
        }
    };

    // Vistorias do LocaSmartCheck do veículo no período, no formato das vistorias da plataforma.
    // A API do Check não filtra por placa nem por data: percorre as páginas (até 1.000 vistorias).
    const buscarVistoriasLocaSmartCheck = async (placa: string | undefined, inicio: Date, fim: Date): Promise<any[]> => {
        if (!placa) return [];
        const status = await api.get('/LocaSmartCheck/Status').catch(() => null);
        if (!status?.data?.integrado) return [];

        const mesmaPlaca = (p: string | undefined) => (p || '').replace(/[^A-Z0-9]/gi, '').toUpperCase() === placa.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        const encontradas: any[] = [];
        const tamanhoPagina = 100;
        for (let pagina = 1; pagina <= 10; pagina++) {
            const resp = await api.get('/LocaSmartCheck/Vistorias', { params: { page: pagina, pageSize: tamanhoPagina } });
            const itens: any[] = Array.isArray(resp.data) ? resp.data : (resp.data?.items || []);
            for (const v of itens) {
                const data = new Date(v.completedAt || v.startedAt || v.createdAt);
                if (mesmaPlaca(v.vehiclePlate) && data >= inicio && data <= fim && v.status !== 'Canceled') {
                    encontradas.push({
                        id: v.id,
                        data: v.completedAt || v.startedAt || v.createdAt,
                        hodometro: v.mileage,
                        status: v.status === 'Completed' ? 1 : 0,
                        obs: [v.templateName, v.notes].filter(Boolean).join(' - '),
                        fotosLocaSmartCheck: (v.items || []).flatMap((item: any) =>
                            (item.photos || []).map((foto: any) => ({ label: item.templateItemName, filePath: foto.filePath }))),
                    });
                }
            }
            if (itens.length < tamanhoPagina) break;
        }
        return encontradas;
    };

    // Foto do LocaSmartCheck só sai pela API autenticada: vira um blob local para o jsPDF desenhar.
    const urlDaFotoLocaSmartCheck = async (filePath: string): Promise<string | null> => {
        try {
            const caminho = filePath.split('/').map(s => encodeURIComponent(s)).join('/');
            const resp = await api.get(`/LocaSmartCheck/Fotos/${caminho}`, { responseType: 'blob' });
            return URL.createObjectURL(resp.data);
        } catch {
            return null;
        }
    };

    const buscarDados = async () => {
        const dados: any = { locacao };
        const tenantId = obterIdTenent(token);

        try {
            // Sempre buscar locação completa para ter todos os campos
            try {
                const resp = await api.get(`/Locacao/${locacao.id || locacao}`);
                dados.locacao = resp.data;
            } catch { }

            const loc = dados.locacao;
            const idLocacao = loc.id;

            // Buscar dados em paralelo conforme seções ativas
            const promises: Promise<void>[] = [];

            if (secoes.find(s => s.key === 'financeiro' && s.ativo)) {
                promises.push(
                    api.get(`/Pagamento/PorLocacao/${idLocacao}`)
                        .then(r => { dados.pagamentos = r.data || []; })
                        .catch(() => { dados.pagamentos = []; })
                );
                promises.push(
                    api.get('/ObterTodosPorContexto', {
                        params: { idEmpresa: tenantId, idLocacao: idLocacao }
                    })
                        .then(r => { dados.lancamentos = r.data || []; })
                        .catch(() => { dados.lancamentos = []; })
                );
            }

            if (secoes.find(s => s.key === 'multas' && s.ativo)) {
                promises.push(
                    api.get('/ObterTodasMultasPorContexto', { params: { idEmpresa: tenantId, status: 5 } })
                        .then(r => { dados.multas = (r.data || []).filter((m: any) => m.idLocacao === idLocacao); })
                        .catch(() => { dados.multas = []; })
                );
            }

            const janela = janelaDaLocacao(loc.dataInicio, loc.dataTermino);

            if (secoes.find(s => s.key === 'manutencoes' && s.ativo)) {
                promises.push(
                    api.get('/servicoVeiculo/filtrar', {
                        params: {
                            idEmpresa: tenantId,
                            idVeiculo: loc.idVeiculo,
                            dataInicial: janela.inicio.toISOString(),
                            dataFinal: janela.fim.toISOString(),
                            page: 1,
                            pageSize: 500,
                        }
                    })
                        .then(r => { dados.manutencoes = r.data?.items || []; })
                        .catch(() => { dados.manutencoes = []; })
                );
            }

            if (secoes.find(s => s.key === 'trocasOleo' && s.ativo)) {
                promises.push(
                    api.get(`/TrocaDeOleo/PorLocacao/${idLocacao}`)
                        .then(r => { dados.trocasOleo = r.data || []; })
                        .catch(() => { dados.trocasOleo = []; })
                );
            }

            if (secoes.find(s => s.key === 'substituicoes' && s.ativo)) {
                promises.push(
                    api.get(`/Locacao/HistoricoSubstituicaoVeiculo/${idLocacao}`)
                        .then(r => { dados.substituicoes = r.data || []; })
                        .catch(() => { dados.substituicoes = []; })
                );
            }

            if (secoes.find(s => s.key === 'vistorias' && s.ativo)) {
                // Une as vistorias vinculadas a esta locacao (fluxo padrao) com as do veiculo no periodo
                // (cobre vistoria sem motorista indicado, idVeiculo null, etc) e as do LocaSmartCheck,
                // que ficam no sistema externo e nunca entram nas tabelas da plataforma (DL-161).
                // Janela por instante com folga (ver janelaDaLocacao): a retirada e a devolução agora têm hora.
                const paramsPeriodo = { idVeiculo: loc.idVeiculo, dataInicio: janela.inicio.toISOString(), dataFim: janela.fim.toISOString() };
                let houveFalha = false;
                const buscar = (url: string, config?: any) => api.get(url, config).catch(() => { houveFalha = true; return { data: [] }; });
                promises.push(
                    Promise.all([
                        buscar(`/Vistoria/Carro/PorLocacao/${idLocacao}`),
                        buscar(`/Vistoria/Moto/PorLocacao/${idLocacao}`),
                        buscar('/Vistoria/Carro/PorVeiculoEPeriodo', { params: paramsPeriodo }),
                        buscar('/Vistoria/Moto/PorVeiculoEPeriodo', { params: paramsPeriodo }),
                        buscarVistoriasLocaSmartCheck(loc?.veiculo?.placa, janela.inicio, janela.fim)
                            .catch(() => { houveFalha = true; return [] as any[]; }),
                    ]).then(([carrosLoc, motosLoc, carrosPer, motosPer, doCheck]) => {
                        const todas = [
                            ...(carrosLoc.data || []),
                            ...(motosLoc.data || []),
                            ...(carrosPer.data || []),
                            ...(motosPer.data || []),
                            ...doCheck,
                        ];
                        const vistos = new Set<string>();
                        dados.vistorias = todas.filter((v: any) => {
                            if (!v?.id || vistos.has(v.id)) return false;
                            vistos.add(v.id);
                            return true;
                        });
                        if (houveFalha) {
                            toast.warning('Não foi possível buscar todas as vistorias (sem permissão ou falha na consulta). A seção de vistorias pode estar incompleta.');
                        }
                    })
                );
            }

            await Promise.all(promises);
        } catch (error) {
            toast.error('Erro ao buscar dados para o relatório');
        }

        return dados;
    };

    const gerarPDF = async () => {
        if (secoesAtivas.length === 0) {
            toast.info('Selecione ao menos uma seção para o relatório');
            return;
        }

        setGerando(true);

        try {
            const dados = await buscarDados();
            const loc = dados.locacao;
            const locatario = loc?.locatario;
            const veiculo = loc?.veiculo;

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const largura = pdf.internal.pageSize.width;
            const altura = pdf.internal.pageSize.height;
            const margem = 12;
            const areaUtil = largura - 2 * margem;
            let y = margem;

            const paleta = {
                primaria: '#1A202C',
                secundaria: '#64748B',
                borda: '#E2E8F0',
                destaque: '#1E3A5F',
                fundoSecao: '#F1F5F9',
                totalFundo: '#1E3A5F',
                totalTexto: '#FFFFFF',
            };

            // Rodape em todas as paginas
            (pdf as any).autoTableSetDefaults({
                didDrawPage: () => {
                    const rodapeY = altura - 12;
                    pdf.setDrawColor(paleta.borda);
                    pdf.setLineWidth(0.3);
                    pdf.line(margem, rodapeY, largura - margem, rodapeY);
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(7);
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text(`Emitido em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, margem, rodapeY + 4);
                    const pageCount = (pdf as any).internal.getNumberOfPages();
                    pdf.text(`Página ${pdf.getCurrentPageInfo().pageNumber} de ${pageCount}`, largura - margem - 25, rodapeY + 4);
                },
            });

            // === CABECALHO ===
            let deslocamentoTitulo = 24;
            try {
                const logoUrl = await obterLogoDaEmpresa();
                const img = new Image();
                img.crossOrigin = 'anonymous';
                await new Promise<void>((resolve) => {
                    img.onload = () => { deslocamentoTitulo = adicionarLogo(pdf, img, { x: margem, y, largura: 36, altura: 20 }).largura + 4; resolve(); };
                    img.onerror = () => resolve();
                    img.src = logoUrl;
                });
            } catch { }

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(16);
            pdf.setTextColor(paleta.destaque);
            pdf.text('RELATÓRIO COMPLETO DA LOCAÇÃO', margem + deslocamentoTitulo, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(paleta.secundaria);
            const veiculoResumo = veiculo ? `${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}` : '';
            const locatarioResumo = locatario ? locatario.nome : '';
            pdf.text(`${veiculoResumo}  |  ${locatarioResumo}`, margem + 24, y + 14);
            y += 26;

            pdf.setDrawColor(paleta.destaque);
            pdf.setLineWidth(0.8);
            pdf.line(margem, y, largura - margem, y);
            y += 6;

            // === HELPERS ===
            const verificarEspaco = (espacoNecessario: number) => {
                if (y + espacoNecessario > altura - 20) {
                    pdf.addPage();
                    y = margem + 5;
                }
            };

            const tituloSecao = (numero: number, texto: string) => {
                verificarEspaco(12);
                pdf.setFillColor(paleta.fundoSecao);
                pdf.roundedRect(margem, y, areaUtil, 8, 1, 1, 'F');
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(10);
                pdf.setTextColor(paleta.destaque);
                pdf.text(`${numero}. ${texto}`, margem + 3, y + 5.5);
                y += 12;
            };

            const infoLinha = (label: string, valor: string, col2Label?: string, col2Valor?: string) => {
                verificarEspaco(6);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(paleta.primaria);
                pdf.text(label, margem + 2, y);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(paleta.secundaria);
                pdf.text(valor || '-', margem + 42, y);

                if (col2Label) {
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(paleta.primaria);
                    pdf.text(col2Label, margem + areaUtil / 2 + 2, y);
                    pdf.setFont('helvetica', 'normal');
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text(col2Valor || '-', margem + areaUtil / 2 + 42, y);
                }
                y += 5;
            };

            let secaoNumero = 0;

            // === BADGE DE STATUS (sempre visível) ===
            const statusTexto = statusLocacao(loc.status);
            const statusCores: Record<number, { bg: string; texto: string }> = {
                1: { bg: '#4CAF50', texto: '#FFFFFF' }, // Ativa
                2: { bg: '#FFC107', texto: '#1A202C' }, // Inativa
                3: { bg: '#f44336', texto: '#FFFFFF' }, // Encerrada
            };
            const corStatus = statusCores[loc.status] || { bg: '#9E9E9E', texto: '#FFFFFF' };

            verificarEspaco(14);
            pdf.setFillColor(corStatus.bg);
            pdf.roundedRect(margem, y, areaUtil, 10, 2, 2, 'F');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(corStatus.texto);
            pdf.text(`STATUS: ${statusTexto.toUpperCase()}`, margem + areaUtil / 2, y + 6.5, { align: 'center' });
            y += 14;

            // === 1. DADOS DA LOCACAO ===
            if (secoes.find(s => s.key === 'dadosLocacao' && s.ativo)) {
                secaoNumero++;
                tituloSecao(secaoNumero, 'DADOS DA LOCAÇÃO');
                const diasSemana: Record<number, string> = { 0: 'Domingo', 1: 'Segunda-feira', 2: 'Terça-feira', 3: 'Quarta-feira', 4: 'Quinta-feira', 5: 'Sexta-feira', 6: 'Sábado' };
                infoLinha('Periodicidade:', periodicidade(loc.periodicidadePagamento), 'Dia Pagamento:', diasSemana[loc.diaPagamento] || '-');
                infoLinha('Início:', formatarDataHoraLocacao(loc.dataInicio), 'Término:', formatarDataHoraLocacao(loc.dataTermino));
                infoLinha('Aluguel:', formatarMoeda(loc.valorAluguel), `${nomeCaucao.titulo}:`, formatarMoeda(loc.valorCaucao));
                infoLinha('KM Inicial:', String(loc.kmInicial || '-'), 'KM Final:', String(loc.kmFinal || '-'));
                if (loc.jurosAoMes) infoLinha('Juros ao Mês:', `${loc.jurosAoMes}%`, 'Multa Fixa:', formatarMoeda(loc.multaFixa));
                y += 3;
            }

            // === 2. LOCATARIO ===
            if (secoes.find(s => s.key === 'locatario' && s.ativo) && locatario) {
                secaoNumero++;
                tituloSecao(secaoNumero, 'LOCATÁRIO');
                infoLinha('Nome:', locatario.nome, 'Documento:', locatario.documento);
                infoLinha('Telefone:', locatario.telefone || '-', 'Email:', locatario.email || '-');
                if (locatario.cidade) infoLinha('Cidade:', `${locatario.cidade}/${locatario.estado}`, 'Bairro:', locatario.bairro || '-');
                if (locatario.logradouro) infoLinha('Endereco:', `${locatario.logradouro}, ${locatario.numero || 's/n'}`, 'CEP:', locatario.cep || '-');
                if (locatario.vencimentoHabilitacao) infoLinha('Venc. CNH:', formatarData(locatario.vencimentoHabilitacao), 'RG:', locatario.rg || '-');
                y += 3;
            }

            // === 3. VEICULO ===
            if (secoes.find(s => s.key === 'veiculo' && s.ativo) && veiculo) {
                secaoNumero++;
                tituloSecao(secaoNumero, 'VEÍCULO');
                infoLinha('Placa:', veiculo.placa, 'Marca/Modelo:', `${veiculo.marca} ${veiculo.modelo}`);
                infoLinha('Ano:', veiculo.ano, 'Cor:', veiculo.cor || '-');
                infoLinha('Hodômetro:', veiculo.hodometro ? `${veiculo.hodometro} km` : '-', 'Renavam:', veiculo.renavam || '-');
                if (veiculo.chassi) infoLinha('Chassi:', veiculo.chassi, '', '');
                y += 3;
            }

            // === SUBSTITUIÇÕES DE VEÍCULO ===
            if (secoes.find(s => s.key === 'substituicoes' && s.ativo)) {
                const substituicoes = dados.substituicoes || [];
                if (substituicoes.length > 0) {
                    secaoNumero++;
                    tituloSecao(secaoNumero, 'SUBSTITUIÇÕES DE VEÍCULO');
                    verificarEspaco(20);
                    autoTable(pdf, {
                        startY: y,
                        margin: { left: margem, right: margem },
                        head: [['Data', 'Veículo que saiu', 'KM na saída', 'Veículo que entrou', 'KM na entrada']],
                        body: substituicoes.map((s: any) => [
                            formatarData(s.dataSubstituicao),
                            s.veiculoAnterior || '-',
                            s.kmFinalVeiculoAnterior != null ? `${Number(s.kmFinalVeiculoAnterior).toLocaleString('pt-BR')} km` : '-',
                            s.veiculoNovo || '-',
                            s.kmInicialVeiculoNovo != null ? `${Number(s.kmInicialVeiculoNovo).toLocaleString('pt-BR')} km` : '-',
                        ]),
                        theme: 'striped',
                        headStyles: { fillColor: paleta.destaque, fontSize: 8 },
                        bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 6;
                }
            }

            // === 4. CONTRATO E OBSERVACOES ===
            if (secoes.find(s => s.key === 'contrato' && s.ativo)) {
                secaoNumero++;
                tituloSecao(secaoNumero, 'CONTRATO E OBSERVAÇÕES');
                infoLinha('Fiador:', loc.fiador || 'Não informado', 'Opção de Compra:', loc.opcaoDeCompra ? 'Sim' : 'Não');
                if (loc.observacao) {
                    const linhasObs = pdf.splitTextToSize(loc.observacao, areaUtil - 12);
                    const alturaBox = linhasObs.length * 4 + 10;
                    verificarEspaco(alturaBox + 4);

                    // Box amarelo de destaque
                    pdf.setFillColor('#FFF9DB');
                    pdf.setDrawColor('#FFC107');
                    pdf.setLineWidth(0.5);
                    pdf.roundedRect(margem, y, areaUtil, alturaBox, 2, 2, 'FD');

                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(8);
                    pdf.setTextColor('#E67700');
                    pdf.text('OBSERVAÇÕES', margem + 4, y + 5);

                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(7.5);
                    pdf.setTextColor(paleta.primaria);
                    pdf.text(linhasObs, margem + 4, y + 10);
                    y += alturaBox + 4;
                }
                y += 3;
            }

            // === FINANCEIRO (unificado: pagamentos + lançamentos) ===
            if (secoes.find(s => s.key === 'financeiro' && s.ativo)) {
                const pagamentos = dados.pagamentos || [];
                const lancamentos = dados.lancamentos || [];

                const pgEmAtraso = pagamentos.filter((p: any) => p.status === 3);
                const pgAVencer = pagamentos.filter((p: any) => p.status === 2);
                const pgPagos = pagamentos.filter((p: any) => p.status === 1);
                const lcEntradas = lancamentos.filter((l: any) => l.tipo === 1);
                const lcSaidas = lancamentos.filter((l: any) => l.tipo === 2);
                const lcResolvidos = lancamentos.filter((l: any) => l.status === 1);
                const lcPendentes = lancamentos.filter((l: any) => l.status === 2);

                const totalAtraso = pgEmAtraso.reduce((a: number, p: any) => a + (p.valor || 0), 0);
                const totalAVencer = pgAVencer.reduce((a: number, p: any) => a + (p.valor || 0), 0);
                const totalPago = pgPagos.reduce((a: number, p: any) => a + (p.valor || 0), 0);
                const totalEntradas = lcEntradas.reduce((a: number, l: any) => a + (l.valor || 0), 0);
                const totalSaidas = lcSaidas.reduce((a: number, l: any) => a + (l.valor || 0), 0);

                secaoNumero++;
                tituloSecao(secaoNumero, 'FINANCEIRO');

                // Badges resumo
                verificarEspaco(16);
                const badgeLargura = (areaUtil - 16) / 5;
                const badges = [
                    { label: 'EM ATRASO', valor: totalAtraso, qtd: pgEmAtraso.length, cor: '#f44336' },
                    { label: 'A VENCER', valor: totalAVencer, qtd: pgAVencer.length, cor: '#FF9800' },
                    { label: 'PAGO', valor: totalPago, qtd: pgPagos.length, cor: '#4CAF50' },
                    { label: 'ENTRADAS', valor: totalEntradas, qtd: lcEntradas.length, cor: '#2196F3' },
                    { label: 'SAÍDAS', valor: totalSaidas, qtd: lcSaidas.length, cor: '#9C27B0' },
                ];
                badges.forEach((badge, i) => {
                    const bx = margem + i * (badgeLargura + 4);
                    pdf.setFillColor(badge.cor);
                    pdf.roundedRect(bx, y, badgeLargura, 12, 2, 2, 'F');
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(6);
                    pdf.setTextColor('#FFFFFF');
                    pdf.text(badge.label, bx + badgeLargura / 2, y + 4, { align: 'center' });
                    pdf.setFontSize(7);
                    pdf.text(`${formatarMoeda(badge.valor)} (${badge.qtd})`, bx + badgeLargura / 2, y + 9, { align: 'center' });
                });
                y += 18;

                // Tabela: Pagamentos em atraso
                if (pgEmAtraso.length > 0) {
                    verificarEspaco(15);
                    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor('#f44336');
                    pdf.text('Pagamentos em Atraso', margem + 2, y); y += 4;
                    autoTable(pdf, {
                        startY: y, margin: { left: margem, right: margem },
                        head: [['Data Prevista', 'Descrição', 'Valor']],
                        body: pgEmAtraso.map((p: any) => [formatarData(p.dataPrevista), p.descricao || 'Aluguel', formatarMoeda(p.valor)]),
                        theme: 'striped', headStyles: { fillColor: '#f44336', fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 4;
                }

                // Tabela: Pagamentos a vencer
                if (pgAVencer.length > 0) {
                    verificarEspaco(15);
                    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor('#E67700');
                    pdf.text('Pagamentos a Vencer', margem + 2, y); y += 4;
                    autoTable(pdf, {
                        startY: y, margin: { left: margem, right: margem },
                        head: [['Data Prevista', 'Descrição', 'Valor']],
                        body: pgAVencer.map((p: any) => [formatarData(p.dataPrevista), p.descricao || 'Aluguel', formatarMoeda(p.valor)]),
                        theme: 'striped', headStyles: { fillColor: '#FFC107', textColor: '#1A202C', fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 4;
                }

                // Tabela: Pagamentos realizados
                if (pgPagos.length > 0) {
                    verificarEspaco(15);
                    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor('#4CAF50');
                    pdf.text('Pagamentos Realizados', margem + 2, y); y += 4;
                    autoTable(pdf, {
                        startY: y, margin: { left: margem, right: margem },
                        head: [['Data Prevista', 'Pago em', 'Valor']],
                        body: pgPagos.map((p: any) => [formatarData(p.dataPrevista), formatarData(p.dataPagamentoRealizado), formatarMoeda(p.valor)]),
                        theme: 'striped', headStyles: { fillColor: '#4CAF50', fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 4;
                }

                // Tabela: Lançamentos
                if (lancamentos.length > 0) {
                    verificarEspaco(15);
                    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(paleta.destaque);
                    pdf.text('Lançamentos', margem + 2, y); y += 4;
                    autoTable(pdf, {
                        startY: y, margin: { left: margem, right: margem },
                        head: [['Data', 'Descrição', 'Tipo', 'Valor', 'Status']],
                        body: lancamentos.map((l: any) => [
                            formatarData(l.dataRealizado), (l.descricao || '-').substring(0, 50),
                            l.tipo === 1 ? 'Entrada' : 'Saída', formatarMoeda(l.valor),
                            l.status === 1 ? 'Resolvido' : 'Pendente',
                        ]),
                        theme: 'striped', headStyles: { fillColor: paleta.destaque, fontSize: 8 }, bodyStyles: { fontSize: 7 },
                        didParseCell: (data: any) => {
                            if (data.section === 'body' && data.column.index === 2) {
                                data.cell.styles.textColor = data.cell.raw === 'Entrada' ? '#4CAF50' : '#f44336';
                                data.cell.styles.fontStyle = 'bold';
                            }
                        },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 4;
                }

                if (pagamentos.length === 0 && lancamentos.length === 0) {
                    pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8); pdf.setTextColor(paleta.secundaria);
                    pdf.text('Nenhum registro financeiro.', margem + 2, y); y += 8;
                }
                y += 2;
            }

            // === MULTAS ===
            if (secoes.find(s => s.key === 'multas' && s.ativo)) {
                const multas = dados.multas || [];
                secaoNumero++;
                tituloSecao(secaoNumero, 'MULTAS');
                if (multas.length > 0) {
                    verificarEspaco(20);
                    autoTable(pdf, {
                        startY: y,
                        margin: { left: margem, right: margem },
                        head: [['Data', 'Descrição', 'Valor', 'Status']],
                        body: multas.map((m: any) => [
                            formatarData(m.dataHoraCometimento),
                            m.descricaoInfracao,
                            formatarMoeda(m.valor),
                            statusMulta(m.status),
                        ]),
                        theme: 'striped',
                        headStyles: { fillColor: paleta.destaque, fontSize: 8 },
                        bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 6;
                } else {
                    pdf.setFont('helvetica', 'italic');
                    pdf.setFontSize(8);
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text('Nenhuma multa registrada.', margem + 2, y);
                    y += 8;
                }
            }

            // === 8. MANUTENCOES ===
            if (secoes.find(s => s.key === 'manutencoes' && s.ativo)) {
                const manutencoes = dados.manutencoes || [];
                secaoNumero++;
                tituloSecao(secaoNumero, 'MANUTENÇÕES');
                if (manutencoes.length > 0) {
                    verificarEspaco(20);
                    autoTable(pdf, {
                        startY: y,
                        margin: { left: margem, right: margem },
                        head: [['Data', 'Descrição', 'Valor', 'Hodômetro']],
                        body: manutencoes.map((m: any) => [
                            formatarData(m.dataRealizado),
                            m.descricao || '-',
                            formatarMoeda(m.valor),
                            m.hodometro ? `${m.hodometro} km` : '-',
                        ]),
                        theme: 'striped',
                        headStyles: { fillColor: paleta.destaque, fontSize: 8 },
                        bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 6;
                } else {
                    pdf.setFont('helvetica', 'italic');
                    pdf.setFontSize(8);
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text('Nenhuma manutenção registrada.', margem + 2, y);
                    y += 8;
                }
            }

            // === 9. TROCAS DE OLEO ===
            if (secoes.find(s => s.key === 'trocasOleo' && s.ativo)) {
                const trocas = dados.trocasOleo || loc.trocasDeOleo || [];
                secaoNumero++;
                tituloSecao(secaoNumero, 'TROCAS DE ÓLEO');
                if (trocas.length > 0) {
                    verificarEspaco(20);
                    autoTable(pdf, {
                        startY: y,
                        margin: { left: margem, right: margem },
                        head: [['Data', 'Hodômetro', 'Observação']],
                        body: trocas.map((t: any) => [
                            formatarData(t.data),
                            t.hodometro ? `${t.hodometro} km` : '-',
                            t.obs || '-',
                        ]),
                        theme: 'striped',
                        headStyles: { fillColor: paleta.destaque, fontSize: 8 },
                        bodyStyles: { fontSize: 7.5 },
                    });
                    y = (pdf as any).lastAutoTable.finalY + 6;
                } else {
                    pdf.setFont('helvetica', 'italic');
                    pdf.setFontSize(8);
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text('Nenhuma troca de óleo registrada.', margem + 2, y);
                    y += 8;
                }
            }

            // === 10. VISTORIAS ===
            if (secoes.find(s => s.key === 'vistorias' && s.ativo)) {
                const vistorias = dados.vistorias || [...(loc.vistoriasCarros || []), ...(loc.vistoriasMotos || [])];
                secaoNumero++;
                tituloSecao(secaoNumero, 'VISTORIAS');
                if (vistorias.length > 0) {
                    for (const vistoria of vistorias) {
                        verificarEspaco(15);
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(8);
                        pdf.setTextColor(paleta.primaria);
                        pdf.text(`Vistoria de ${formatarData(vistoria.data)} - ${vistoria.hodometro ? vistoria.hodometro + ' km' : ''} - ${vistoria.status === 1 ? 'Realizada' : 'Pendente'}`, margem + 2, y);
                        y += 4;
                        if (vistoria.obs) {
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(7.5);
                            pdf.setTextColor(paleta.secundaria);
                            pdf.text(`Obs: ${vistoria.obs}`, margem + 2, y);
                            y += 4;
                        }

                        // Fotos da vistoria
                        const fotos: { label: string; url: string }[] = [];
                        const camposFoto = [
                            { key: 'fotoFrente', label: 'Frente' },
                            { key: 'fotoTraseira', label: 'Traseira' },
                            { key: 'fotoLateralEsquerda', label: 'Lat. Esquerda' },
                            { key: 'fotoLateralDireita', label: 'Lat. Direita' },
                            { key: 'fotoMotor', label: 'Motor' },
                            { key: 'fotoPainel', label: 'Painel' },
                            { key: 'fotoPainelLigado', label: 'Painel Ligado' },
                            { key: 'fotoPortaMalas', label: 'Porta-malas' },
                            { key: 'fotoBancoDianteiro', label: 'Banco Diant.' },
                            { key: 'fotoBancoTraseiro', label: 'Banco Tras.' },
                            { key: 'fotoHodometro', label: 'Hodômetro' },
                            { key: 'fotoPneuDianteiroEsquerdo', label: 'Pneu DE' },
                            { key: 'fotoPneuDianteiroDireito', label: 'Pneu DD' },
                            { key: 'fotoPneuTraseiroEsquerdo', label: 'Pneu TE' },
                            { key: 'fotoPneuTraseiroDireito', label: 'Pneu TD' },
                            { key: 'fotoFrontal', label: 'Frontal' },
                            { key: 'fotoPneuDianteiro', label: 'Pneu Diant.' },
                            { key: 'fotoPneuTraseiro', label: 'Pneu Tras.' },
                        ];
                        camposFoto.forEach(cf => {
                            if (vistoria[cf.key]) fotos.push({ label: cf.label, url: vistoria[cf.key] });
                        });
                        for (const foto of (vistoria.fotosLocaSmartCheck || []).slice(0, 24)) {
                            const url = await urlDaFotoLocaSmartCheck(foto.filePath);
                            if (url) fotos.push({ label: foto.label, url });
                        }

                        if (fotos.length > 0) {
                            const fotoLargura = 40;
                            const fotoAltura = 30;
                            const fotosPerRow = Math.floor(areaUtil / (fotoLargura + 4));
                            let fotoX = margem;
                            let fotosNaLinha = 0;

                            for (const foto of fotos) {
                                verificarEspaco(fotoAltura + 10);
                                try {
                                    const img = new Image();
                                    img.crossOrigin = 'anonymous';
                                    await new Promise<void>((resolve) => {
                                        img.onload = () => {
                                            pdf.addImage(img, 'JPEG', fotoX, y, fotoLargura, fotoAltura);
                                            pdf.setFont('helvetica', 'normal');
                                            pdf.setFontSize(6);
                                            pdf.setTextColor(paleta.secundaria);
                                            pdf.text(foto.label, fotoX + fotoLargura / 2, y + fotoAltura + 3, { align: 'center' });
                                            resolve();
                                        };
                                        img.onerror = () => resolve();
                                        img.src = foto.url;
                                    });
                                } catch { }

                                fotosNaLinha++;
                                if (fotosNaLinha >= fotosPerRow) {
                                    fotoX = margem;
                                    y += fotoAltura + 8;
                                    fotosNaLinha = 0;
                                } else {
                                    fotoX += fotoLargura + 4;
                                }
                            }
                            if (fotosNaLinha > 0) y += fotoAltura + 8;
                        }
                        y += 4;
                    }
                } else {
                    pdf.setFont('helvetica', 'italic');
                    pdf.setFontSize(8);
                    pdf.setTextColor(paleta.secundaria);
                    pdf.text('Nenhuma vistoria registrada.', margem + 2, y);
                    y += 8;
                }
            }

            // Abrir PDF
            const pdfBlob = pdf.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');

            toast.success('Relatório gerado com sucesso!');
            onClose();
        } catch (error) {
            toast.error('Erro ao gerar o relatório');
        } finally {
            setGerando(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header style={{ backgroundColor: '#1E3A5F', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
                    <FaFilePdf /> Relatório Completo da Locação
                </Modal.Title>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.3rem', cursor: 'pointer', opacity: 0.8, padding: '0 0.3rem' }}>
                    &times;
                </button>
            </Modal.Header>
            <Modal.Body style={{ padding: '1.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <p style={{ color: '#64748B', fontSize: '0.9rem', margin: 0 }}>
                        Selecione as seções que deseja incluir no relatório:
                    </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <Button variant="link" size="sm" style={{ color: '#1E3A5F', textDecoration: 'none', fontSize: '0.8rem' }} onClick={() => marcarTodas(true)}>
                        Marcar todas
                    </Button>
                    <Button variant="link" size="sm" style={{ color: '#64748B', textDecoration: 'none', fontSize: '0.8rem' }} onClick={() => marcarTodas(false)}>
                        Desmarcar todas
                    </Button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {secoes.map((secao) => (
                        <div
                            key={secao.key}
                            onClick={() => toggleSecao(secao.key)}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.6rem',
                                padding: '0.7rem',
                                borderRadius: '8px',
                                border: `1.5px solid ${secao.ativo ? '#1E3A5F' : '#E2E8F0'}`,
                                backgroundColor: secao.ativo ? '#F0F7FF' : '#FAFAFA',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <div style={{ marginTop: '2px', color: secao.ativo ? '#1E3A5F' : '#CBD5E1', fontSize: '1.1rem' }}>
                                {secao.ativo ? <FaCheckSquare /> : <FaRegSquare />}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: secao.ativo ? '#1E3A5F' : '#64748B' }}>
                                    {secao.label}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                                    {secao.descricao}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #E2E8F0', padding: '1rem 1.5rem' }}>
                <Button variant="outline-secondary" onClick={onClose} disabled={gerando}>
                    Cancelar
                </Button>
                <Button
                    onClick={gerarPDF}
                    disabled={gerando || secoesAtivas.length === 0}
                    style={{ backgroundColor: '#1E3A5F', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    {gerando ? (
                        <>
                            <Spinner animation="border" size="sm" /> Gerando...
                        </>
                    ) : (
                        <>
                            <FaFilePdf /> Gerar PDF ({secoesAtivas.length} {secoesAtivas.length === 1 ? 'seção' : 'seções'})
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalRelatorioLocacao;
